import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Table, Button, Container, Row, Col, Card, Badge, Modal, Pagination, Form } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';
import './SupportMessages.css';

function SupportMessages() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showAdminNoteModal, setShowAdminNoteModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${BaseUrl}/supporttickets/all`);
      const sortedTickets = response.data.sort((a, b) => {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setTickets(sortedTickets);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAdminNoteUpdate = async (ticketId, adminNotes) => {
    try {
      // First, get the current ticket data
      const currentTicket = tickets.find(ticket => ticket.id === ticketId);
      
      if (!currentTicket) {
        throw new Error('Ticket not found');
      }
      
      // Create update data with all current fields plus the new admin notes
      const updateData = {
        ...currentTicket, // Include all existing fields
        admin_notes: adminNotes // Update only the admin_notes field
      };
      
      await axios.put(`${BaseUrl}/supporttickets/${ticketId}`, updateData);
      
      Swal.fire(
        'Success!',
        'Admin note has been added successfully.',
        'success'
      );
      
      fetchTickets(); // Refresh the list after update
    } catch (err) {
      Swal.fire(
        'Error!',
        err.response?.data?.error || 'Failed to update admin note',
        'error'
      );
    }
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      // First, get the current ticket data
      const currentTicket = tickets.find(ticket => ticket.id === ticketId);
      
      if (!currentTicket) {
        throw new Error('Ticket not found');
      }
      
      // Create update data with all current fields plus the new status
      const updateData = {
        ...currentTicket, // Include all existing fields
        status: newStatus // Update only the status field
      };
      
      await axios.put(`${BaseUrl}/supporttickets/${ticketId}`, updateData);
      
      Swal.fire(
        'Updated!',
        'Ticket status has been updated successfully and a response email has been sent to the user.',
        'success'
      );
      
      fetchTickets(); // Refresh the list after update
    } catch (err) {
      Swal.fire(
        'Error!',
        err.response?.data?.error || 'Failed to update ticket status',
        'error'
      );
    }
  };

  const confirmCloseTicket = (ticket) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to close ticket #${ticket.id}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, close it!'
    }).then((result) => {
      if (result.isConfirmed) {
        handleStatusUpdate(ticket.id, 'closed');
      }
    });
  };

  const handleAdminNoteSubmit = async () => {
    if (!adminNote.trim()) {
      Swal.fire('Error!', 'Please enter a note before submitting.', 'error');
      return;
    }

    try {
      await handleAdminNoteUpdate(selectedTicket.id, adminNote);
      setShowAdminNoteModal(false);
      setAdminNote('');
      setSelectedTicket(null);
    } catch (err) {
      console.error('Error submitting admin note:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge bg="warning">Open</Badge>;
      case 'in_progress':
        return <Badge bg="info">In Progress</Badge>;
      case 'resolved':
        return <Badge bg="success">Resolved</Badge>;
      case 'closed':
        return <Badge bg="secondary">Closed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getUserRoleBadge = (role) => {
    switch (role) {
      case 'employer':
        return <Badge bg="primary">Employer</Badge>;
      case 'job_seeker':
        return <Badge bg="info">Job Seeker</Badge>;
      case 'employee':
        return <Badge bg="info">Job Seeker</Badge>;
      case 'candidate':
        return <Badge bg="info">Candidate</Badge>;
      case 'agency':
        return <Badge bg="warning">Agency</Badge>;
      case 'admin':
        return <Badge bg="success">Admin</Badge>;
      default:
        return <Badge bg="secondary">{role}</Badge>;
    }
  };

  const handleViewMessage = (ticket) => {
    setSelectedTicket(ticket);
    setShowMessageModal(true);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedTicket(null);
  };

  const handleOpenAdminNoteModal = (ticket) => {
    setSelectedTicket(ticket);
    setAdminNote(ticket.admin_notes || '');
    setShowAdminNoteModal(true);
  };

  const handleCloseAdminNoteModal = () => {
    setShowAdminNoteModal(false);
    setAdminNote('');
    setSelectedTicket(null);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTickets = tickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(tickets.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 2; // Show only 2 page numbers
    
    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages if total pages are small
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Pagination.Item>
        );
      }
    } else {
      // Always show first page
      items.push(
        <Pagination.Item
          key={1}
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          1
        </Pagination.Item>
      );

      // Show ellipsis if current page is beyond the first few pages
      if (currentPage > 3) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      // Show current page and surrounding pages
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = startPage; i <= endPage; i++) {
        if (i !== 1 && i !== totalPages) {
          items.push(
            <Pagination.Item
              key={i}
              active={i === currentPage}
              onClick={() => handlePageChange(i)}
            >
              {i}
            </Pagination.Item>
          );
        }
      }

      // Show ellipsis if current page is not near the end
      if (currentPage < totalPages - 2) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      // Always show last page
      items.push(
        <Pagination.Item
          key={totalPages}
          active={totalPages === currentPage}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  if (loading) return <div className="support-messages-loading text-center mt-5">Loading...</div>;
  if (error) return <div className="support-messages-error text-center mt-5 text-danger">Error: {error}</div>;

  return (
    <>
      <AdminNavbar />
      <Container className="support-messages-container" style={{ marginTop: '80px' }}>
        <Row className="support-messages-header mb-4">
          <Col>
            <h2>Support Messages</h2>
            <p className="support-messages-subtitle text-muted">Manage and respond to user support tickets</p>
          </Col>
        </Row>

        <Card className="support-messages-card">
          <Card.Body className="support-messages-card-body">
            <div 
              className={`support-messages-table-responsive ${isDragging ? 'grabbing' : ''}`}
              ref={scrollContainerRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              <Table striped bordered hover responsive className="support-messages-table">
                <thead className="support-messages-thead">
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Mobile Number</th>
                    <th>Email</th>
                    <th>User Role</th>
                    <th>Job Category</th>
                    <th>Nationality</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="support-messages-tbody">
                  {currentTickets.map((ticket, index) => (
                    <tr key={ticket.id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{ticket.name}</td>
                      <td>
                        {ticket.mobile_number ? (
                          <a 
                            href={`https://wa.me/${ticket.mobile_number.replace(/\D/g, '')}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                            title="Open in WhatsApp"
                          >
                            {ticket.mobile_number}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td>
                        <a 
                          href={`https://mail.google.com/mail/?view=cm&fs=1&to=${ticket.email}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          title="Compose in Gmail"
                        >
                          {ticket.email}
                        </a>
                      </td>
                      <td>{ticket.user_role ? ticket.user_role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A'}</td>
                      <td>{ticket.job_category || <span className="text-muted">—</span>}</td>
                      <td>{ticket.nationality || <span className="text-muted">—</span>}</td>
                      <td>{getStatusBadge(ticket.status)}</td>
                      <td>
                        <div className="support-messages-actions d-flex flex-nowrap gap-1">
                          <Button 
                            variant="info" 
                            size="sm"
                            className="support-messages-view-btn"
                            onClick={() => handleViewMessage(ticket)}
                          >
                            View
                          </Button>
                          <Button 
                            variant="secondary" 
                            size="sm"
                            className="support-messages-note-btn"
                            onClick={() => handleOpenAdminNoteModal(ticket)}
                          >
                            Admin Note
                          </Button>
                          <Button 
                            variant="danger" 
                            size="sm"
                            className="support-messages-close-btn"
                            onClick={() => confirmCloseTicket(ticket)}
                          >
                            Close
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="support-messages-pagination-container d-flex justify-content-center mt-3">
                <Pagination className="support-messages-pagination">
                  <Pagination.Prev 
                    disabled={currentPage === 1} 
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="support-messages-pagination-prev"
                  />
                  {renderPaginationItems()}
                  <Pagination.Next 
                    disabled={currentPage === totalPages} 
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="support-messages-pagination-next"
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Message View Modal */}
        <Modal show={showMessageModal} onHide={handleCloseMessageModal} size="lg" className="support-messages-modal">
          <Modal.Header closeButton className="support-messages-modal-header">
            <Modal.Title className="support-messages-modal-title">Support Message Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className="support-messages-modal-body">
            {selectedTicket && (
              <div className="support-messages-modal-content">
                <Row className="support-messages-modal-row mb-3">
                  <Col md={6}>
                    <strong>Name:</strong> {selectedTicket.name}
                  </Col>
                  <Col md={6}>
                    <strong>User Role:</strong> {getUserRoleBadge(selectedTicket.user_role)}
                  </Col>
                </Row>
                <Row className="support-messages-modal-row mb-3">
                  <Col md={6}>
                    <strong>Mobile:</strong> {selectedTicket.mobile_number ? (
                      <a 
                        href={`https://wa.me/${selectedTicket.mobile_number.replace(/\D/g, '')}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {selectedTicket.mobile_number}
                      </a>
                    ) : 'N/A'}
                  </Col>
                  <Col md={6}>
                    <strong>Email:</strong> 
                    <a 
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${selectedTicket.email}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {selectedTicket.email}
                    </a>
                  </Col>
                </Row>
                <Row className="support-messages-modal-row mb-3">
                  <Col md={6}>
                    <strong>Status:</strong> {getStatusBadge(selectedTicket.status)}
                  </Col>
                  <Col md={6}>
                    <strong>Created:</strong> {formatDate(selectedTicket.created_at)}
                  </Col>
                </Row>
                <Row className="support-messages-modal-row mb-3">
                  <Col md={6}>
                    <strong>Job Category:</strong> {selectedTicket.job_category || <span className="text-muted">Not specified</span>}
                  </Col>
                  <Col md={6}>
                    <strong>Nationality:</strong> {selectedTicket.nationality || <span className="text-muted">Not specified</span>}
                  </Col>
                </Row>
                <div className="support-messages-message-container mt-3">
                  <strong>Message:</strong>
                  <div className="support-messages-message-content p-3 mt-2 bg-light rounded">
                    {selectedTicket.message}
                  </div>
                </div>
                {selectedTicket.admin_notes && (
                  <div className="support-messages-notes-container mt-3">
                    <strong>Admin Notes:</strong>
                    <div className="support-messages-admin-notes p-3 mt-2 bg-light rounded">
                      {selectedTicket.admin_notes}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="support-messages-modal-footer">
            <Button variant="secondary" onClick={handleCloseMessageModal} className="support-messages-modal-close-btn">
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Admin Note Modal */}
        <Modal show={showAdminNoteModal} onHide={handleCloseAdminNoteModal} size="lg" className="support-messages-admin-modal">
          <Modal.Header closeButton className="support-messages-admin-modal-header">
            <Modal.Title className="support-messages-admin-modal-title">Add Admin Note</Modal.Title>
          </Modal.Header>
          <Modal.Body className="support-messages-admin-modal-body">
            {selectedTicket && (
              <div className="support-messages-admin-modal-content">
                <Row className="support-messages-admin-modal-row mb-3">
                  <Col md={6}>
                    <strong>Ticket ID:</strong> #{selectedTicket.id}
                  </Col>
                  <Col md={6}>
                    <strong>User:</strong> {selectedTicket.name}
                  </Col>
                </Row>
                <Row className="support-messages-admin-modal-row mb-3">
                  <Col md={6}>
                    <strong>Status:</strong> {getStatusBadge(selectedTicket.status)}
                  </Col>
                  <Col md={6}>
                    <strong>Created:</strong> {formatDate(selectedTicket.created_at)}
                  </Col>
                </Row>
                <Form.Group className="support-messages-admin-form-group mb-3">
                  <Form.Label className="support-messages-admin-form-label"><strong>Admin Notes:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Enter your response or notes here..."
                    className="support-messages-admin-textarea"
                  />
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="support-messages-admin-modal-footer">
            <Button variant="secondary" onClick={handleCloseAdminNoteModal} className="support-messages-admin-cancel-btn">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAdminNoteSubmit} className="support-messages-admin-submit-btn">
              Submit Note
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

export default SupportMessages;
